import React, { useEffect, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router";

import { StatusCode } from "api/protocols";

import { Select } from "components/select/index";
import Card from "components/card/Card";
import Form from "components/form/Form";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input/Input";
import Page from "components/page/Page";

import { isEqualWith } from "lodash";
import { useTranslation } from "react-i18next";
import { ICustomer } from "redux/reducers/models/customer";
import { GetCustomer, GetCustomerPaymentMethod, PostCustomer } from "api/rpc/2024-04/facilityAdmin/customer/customer";
import NewCustomer, { ICustomerInfoState } from "components/newCustomer/NewCustomer";
import { showError } from "redux/actions/ui";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import ChangeCustomer from "components/changeCustomer/ChangeCustomer";
import Callout from "components/callout/Callout";
import BatchOptionGroup from "./BatchOptionGroup";
import { GetAccount, PutAccount } from "api/rpc/2024-04/facilityAdmin/client/accounts/accounts";
import { IPutAccount } from "api/rpc/2024-04/facilityAdmin/client/accounts";
import { GetAccountingReference } from "api/rpc/2024-04/facilityAdmin/facility/accounting";
import { formatAccountingReferenceLabel } from "utils/formatAccountingReferenceLabel";
import { capitalize } from "helpers/Helpers";
import PaymentMethodIcon from "components/paymentMethodIcon/PaymentMethodIcon";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen, faPlus, faTrashCan } from "@fortawesome/pro-regular-svg-icons";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import "./accounts.scss";

type TAccountPaymentMethod = {
  id: number;
  brand?: string;
  last4?: string;
  type?: string;
};

interface IAccount {
  id: number;
  title: string;
  account_number: string;
  limit: string;
  limit_is_valid: boolean;
  accounting_reference_id: number;
  customer: ICustomer;
  customers: ICustomer[];
  global: boolean;
  sendStatement: boolean;
  auto_pay_anchor_date: number;
  auto_pay: boolean;
  default_payment_method_id: number | null;
}

type TCustomerSheetMode = "add" | "replace";

interface IChangeCustomerState {
  showChangeCustomer: boolean;
  showNewCustomer: boolean;
  customerQuery: string;
  customerSearching: boolean;
  customerSearchResult: Array<ICustomer>;
  selectedCustomer: ICustomer | null;
  currentCustomer: ICustomer | null;
  mode: TCustomerSheetMode;
  replaceCustomerId: number | null;
}

const MAX_ACCOUNT_CUSTOMERS = 6;
const minimumDayInput = 1;
const maximumDayInput = 31;

function parseCreatedCustomer(body: unknown): ICustomer | null {
  if (body !== null && typeof body === "object") {
    if ("data" in body) {
      const inner = (body as { data: unknown }).data;
      if (inner !== null && typeof inner === "object" && "id" in inner) {
        return inner as ICustomer;
      }
    }
    if ("id" in body) {
      return body as ICustomer;
    }
  }
  return null;
}

function getCustomerInitials(customer: ICustomer): string {
  if (customer.initials?.trim()) {
    return customer.initials.trim().slice(0, 2).toUpperCase();
  }

  const first = customer.first_name?.charAt(0) ?? "";
  const last = customer.last_name?.charAt(0) ?? "";
  const initials = `${first}${last}`.toUpperCase();
  if (initials) {
    return initials;
  }

  return customer.full_name?.trim()?.charAt(0)?.toUpperCase() || "?";
}

function paymentMethodLabel(method: TAccountPaymentMethod): string {
  return `${capitalize(method?.brand)} •••• ${method?.last4 ?? ""}`.trim();
}

function PaymentMethodOption(method: TAccountPaymentMethod) {
  return (
    <span
      className="account-payment-method"
      style={{ display: "inline-flex", flexDirection: "row", alignItems: "center", gap: 8 }}
    >
      <PaymentMethodIcon brand={method.brand} size="sm" />
      <span className="account-payment-method__last4">•••• {method.last4 ?? ""}</span>
    </span>
  );
}

export default function AccountEdit() {
  const { accountId } = useParams<{ accountId: string }>();
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();
  const { Option } = Select;
  const dispatch = useAppDispatch();
  const facilityStore = useAppSelector(store => store.facilityStore);
  const showMultiCourseOption = facilityStore?.facility?.is_single_facility === false;
  const [account, setAccount] = useState<IAccount>(undefined);
  const [paymentMethods, setPaymentMethods] = useState<TAccountPaymentMethod[] | null>(null);
  const [paymentMethodsLoading, setPaymentMethodsLoading] = useState(false);
  const backToAccountsUrl = `/admin/settings/accounts${location.search || ""}`;
  const backToOverviewUrl = `/admin/settings/accounts/overview/${String(accountId)}${location.search || ""}`;
  const editAccountUrl = `/admin/settings/accounts/edit/${String(accountId)}${location.search || ""}`;

  const [accountBeforeChanges, setAccountBeforeChanges] = useState<IAccount>(undefined);
  const [accountingReferenceTypes, setAccountingReferenceTypes] = useState(undefined);

  const [changeCustomerState, setChangeCustomerState] = useState<IChangeCustomerState>({
    showChangeCustomer: false,
    showNewCustomer: false,
    customerQuery: "",
    customerSearching: false,
    customerSearchResult: [],
    selectedCustomer: null,
    currentCustomer: null,
    mode: "replace",
    replaceCustomerId: null,
  });
  const [showAddCustomerSearch, setShowAddCustomerSearch] = useState(false);
  const [customerLimitAlertDismissed, setCustomerLimitAlertDismissed] = useState(false);

  useEffect(() => {
    void loadAccount();
    void loadAccountingReferences();
  }, []);

  useEffect(() => {
    if ((account?.customers?.length ?? 0) < MAX_ACCOUNT_CUSTOMERS) {
      setCustomerLimitAlertDismissed(false);
      return;
    }
    setShowAddCustomerSearch(false);
  }, [account?.customers?.length]);

  useEffect(() => {
    const customerId = account?.customer?.id;
    if (!customerId) {
      setPaymentMethods(null);
      setPaymentMethodsLoading(false);
      return;
    }

    let cancelled = false;
    void (async () => {
      setPaymentMethodsLoading(true);
      const res = await GetCustomerPaymentMethod({ customer_id: customerId }, false);
      if (cancelled) {
        return;
      }
      if (res.status !== StatusCode.OK) {
        setPaymentMethods([]);
        setPaymentMethodsLoading(false);
        return;
      }
      const methods = (Array.isArray(res.data) ? res.data : []) as TAccountPaymentMethod[];
      setPaymentMethods(methods);
      setPaymentMethodsLoading(false);

      setAccount(prev => {
        if (!prev) {
          return prev;
        }
        if (
          prev.default_payment_method_id != null &&
          !methods.some(method => method.id === prev.default_payment_method_id)
        ) {
          setAccountBeforeChanges(before => {
            if (before && before.default_payment_method_id === prev.default_payment_method_id) {
              return { ...before, default_payment_method_id: null };
            }
            return before;
          });
          return { ...prev, default_payment_method_id: null };
        }
        return prev;
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [account?.customer?.id]);

  useEffect(() => {
    let mounted = true;
    let timeoutId: NodeJS.Timeout = null;
    if (mounted === true) {
      timeoutId = setTimeout(() => {
        void search(changeCustomerState.customerQuery);
      }, 500);
    }
    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      setChangeCustomerState(prevState => ({ ...prevState, customerSearchResult: [] }));
    };
  }, [changeCustomerState.customerQuery]);

  const search = async (guestSearchQuery: string) => {
    try {
      if (guestSearchQuery === "") {
        setChangeCustomerState(prevState => ({ ...prevState, customerSearchResult: [] }));
        return;
      } else {
        const guestResults = await searchCustomers(guestSearchQuery);
        setChangeCustomerState(prevState => ({
          ...prevState,
          customerSearching: false,
          customerSearchResult: guestResults ?? [],
        }));
      }
    } catch (error) {
      console.log("err", error);
    }
    return;
  };

  async function searchCustomers(params: string) {
    const customerRes = await GetCustomer({ search: params }, false);

    if (customerRes.status !== StatusCode.OK) {
      return;
    }

    return customerRes?.data;
  }

  async function loadAccount() {
    const accountResponse = await GetAccount({ id: Number(accountId) }, true);

    if (
      accountResponse.status !== StatusCode.OK ||
      accountResponse?.data?.length === undefined ||
      accountResponse.data.length === 0
    ) {
      return;
    }

    const limit = accountResponse?.data[0]?.limit?.toString() ?? "";
    const accountData = accountResponse.data[0];
    const customers =
      accountData.customers && accountData.customers.length > 0
        ? accountData.customers
        : accountData.customer
          ? [accountData.customer]
          : [];

    setAccount({
      id: isNaN(parseInt(accountId)) ? 0 : parseInt(accountId),
      title: accountData?.title?.toString() ?? "",
      account_number: accountData?.account_number?.toString() ?? "",
      limit: limit,
      limit_is_valid: stringIsNumber(limit),
      accounting_reference_id:
        accountData?.accounting_reference_id?.toString() === "0"
          ? 0
          : Number(accountData?.accounting_reference_id),
      customer: accountData?.customer ?? customers[0] ?? null,
      customers,
      global: accountData?.global,
      sendStatement: accountData?.send_statement,
      auto_pay: accountData?.auto_pay_balance,
      auto_pay_anchor_date: accountData?.auto_pay_anchor_date,
      default_payment_method_id: accountData?.default_payment_method_id ?? null,
    });
  }

  async function loadAccountingReferences() {
    const accountingResponse = await GetAccountingReference(
      {
        group: "accounts_receivable",
      },
      true,
    );

    if (accountingResponse.status !== StatusCode.OK) {
      return;
    }

    setAccountingReferenceTypes(accountingResponse.data);
  }

  function handleChange(event: any) {
    const id = event?.target?.id;
    const value = event?.target?.value;

    if (id === undefined || value === undefined) {
      return;
    }

    setAccount(prevState => ({ ...prevState, [id]: value }));
  }

  function handleDropDownChange(id: any) {
    setAccount(prevState => ({ ...prevState, accounting_reference_id: id }));
  }

  function stringIsNumber(value: string) {
    if (value === undefined || value.length === 0) {
      return false;
    } else {
      const valueCharacters = value.split("");
      return valueCharacters.every((v: any) => !Number.isNaN(Number(v)));
    }
  }

  function handleNumberChange(
    event: React.ChangeEvent<HTMLInputElement>,
    propertyToChange: string,
    validFlagToChange: string,
  ) {
    const value = event?.target?.value;

    if (value === undefined) {
      return;
    }

    setAccount(prevState => ({
      ...prevState,
      [propertyToChange]: value,
      [validFlagToChange]: stringIsNumber(value),
    }));
  }

  async function saveAccount() {
    if (account.title === "" || account.account_number === "" || !account.limit_is_valid) {
      return;
    }

    if (account.auto_pay && !account.default_payment_method_id) {
      dispatch(showError(t("Select a default payment method for auto pay")));
      return;
    }

    const putAccount: IPutAccount = {
      id: account.id,
      title: account.title,
      account_number: account.account_number,
      limit: Number(account.limit),
      accounting_reference_id: account.accounting_reference_id === 0 ? null : Number(account.accounting_reference_id),
      customer_id: account.customer === null ? null : account.customer.id,
      customer_ids: account.customers.map(customer => customer.id),
      global: account.global,
      send_statement: account.sendStatement,
      auto_pay_balance: account.auto_pay,
      auto_pay_anchor_date: account.auto_pay_anchor_date,
      default_payment_method_id: account.default_payment_method_id,
    };

    const putAccountResponse = await PutAccount(putAccount, true);

    if (putAccountResponse.status !== StatusCode.OK) {
      dispatch(showError(putAccountResponse.message || t("Error saving account")));
      return;
    }

    history.push(backToOverviewUrl);
  }

  function unsavedChangesExist() {
    if (accountBeforeChanges === undefined) {
      if (account && accountingReferenceTypes) {
        setAccountBeforeChanges(account);
      }
      return false;
    }

    return !isEqualWith(accountBeforeChanges, account);
  }

  function cancelUnsavedChanges() {
    setAccount(accountBeforeChanges);
  }

  function closeChangePlayer() {
    setChangeCustomerState(prevState => ({
      ...prevState,
      showNewCustomer: false,
      currentCustomer: null,
      showChangeCustomer: false,
      customerQuery: "",
      customerSearchResult: [],
      selectedCustomer: null,
      mode: "replace",
      replaceCustomerId: null,
    }));
  }

  function closeAddCustomerSearch() {
    setShowAddCustomerSearch(false);
    setChangeCustomerState(prevState => ({
      ...prevState,
      showNewCustomer: false,
      customerQuery: "",
      customerSearchResult: [],
      selectedCustomer: null,
    }));
  }

  function addCustomerToAccount(selectedCustomer: ICustomer) {
    setAccount(prevState => {
      const existingCustomers = prevState.customers ?? [];

      if (existingCustomers.some(customer => customer.id === selectedCustomer.id)) {
        return prevState;
      }
      if (existingCustomers.length >= MAX_ACCOUNT_CUSTOMERS) {
        return prevState;
      }

      const customers = [...existingCustomers, selectedCustomer];
      const hadPrimary = !!prevState.customer?.id;
      return {
        ...prevState,
        customers,
        customer: hadPrimary ? prevState.customer : selectedCustomer,
        default_payment_method_id: hadPrimary ? prevState.default_payment_method_id : null,
      };
    });
  }

  function applyCustomerSelection() {
    const selectedCustomer = changeCustomerState.selectedCustomer;
    if (!selectedCustomer) {
      return;
    }

    if (changeCustomerState.mode === "add") {
      addCustomerToAccount(selectedCustomer);
      closeChangePlayer();
      return;
    }

    setAccount(prevState => {
      const existingCustomers = prevState.customers ?? [];
      const replaceCustomerId = changeCustomerState.replaceCustomerId ?? changeCustomerState.currentCustomer?.id;
      if (!replaceCustomerId) {
        return prevState;
      }

      if (
        existingCustomers.some(customer => customer.id === selectedCustomer.id) &&
        selectedCustomer.id !== replaceCustomerId
      ) {
        return prevState;
      }

      const customers = existingCustomers.map(customer =>
        customer.id === replaceCustomerId ? selectedCustomer : customer,
      );
      const isPrimary = prevState.customer?.id === replaceCustomerId;

      return {
        ...prevState,
        customers,
        customer: isPrimary ? selectedCustomer : prevState.customer,
        default_payment_method_id: isPrimary ? null : prevState.default_payment_method_id,
      };
    });

    closeChangePlayer();
  }

  function handleAddCustomerSelection(_id: number, customer: ICustomer) {
    addCustomerToAccount(customer);
    closeAddCustomerSearch();
  }

  function removeAccountCustomer(customerId: number) {
    setAccount(prevState => {
      const customers = (prevState.customers ?? []).filter(customer => customer.id !== customerId);
      const isPrimary = prevState.customer?.id === customerId;
      return {
        ...prevState,
        customers,
        customer: isPrimary ? customers[0] ?? null : prevState.customer,
        default_payment_method_id: isPrimary ? null : prevState.default_payment_method_id,
      };
    });
  }

  function handleShowCustomerChange(customer: ICustomer) {
    setShowAddCustomerSearch(false);
    setChangeCustomerState(prevState => ({
      ...prevState,
      showChangeCustomer: true,
      showNewCustomer: false,
      customerQuery: "",
      customerSearchResult: [],
      currentCustomer: customer,
      mode: "replace",
      replaceCustomerId: customer?.id ?? null,
    }));
  }

  function openAddCustomerSearch() {
    if ((account?.customers?.length ?? 0) >= MAX_ACCOUNT_CUSTOMERS) {
      return;
    }

    setShowAddCustomerSearch(true);
    setChangeCustomerState(prevState => ({
      ...prevState,
      showChangeCustomer: false,
      showNewCustomer: false,
      customerQuery: "",
      customerSearchResult: [],
      currentCustomer: null,
      selectedCustomer: null,
      mode: "add",
      replaceCustomerId: null,
    }));
  }

  async function createNewCustomer(customerInfo: ICustomerInfoState) {
    const inputError =
      customerInfo.firstName === "" ||
      customerInfo.lastName === "" ||
      (customerInfo.emailAddress === "" && customerInfo.phoneNumber === "");

    if (inputError) {
      //uiActions.showError(t("Please fill all fields"));
    }

    const customerRes = await PostCustomer(
      {
        first_name: customerInfo.firstName,
        last_name: customerInfo.lastName,
        phone: customerInfo.phoneNumber || null,
        email: customerInfo.emailAddress || null,
        send_welcome_email: customerInfo.sendWelcomeEmail,
      },
      true,
    );

    if (customerRes.status !== StatusCode.OK) {
      dispatch(showError("Error creating new customer"));
      return;
    }
    const newCustomer = parseCreatedCustomer(customerRes.data);
    if (newCustomer === null) {
      dispatch(showError("Error creating new customer"));
      return;
    }

    if (changeCustomerState.showChangeCustomer) {
      setChangeCustomerState(prevState => ({
        ...prevState,
        showNewCustomer: false,
        selectedCustomer: newCustomer,
      }));
      return;
    }

    addCustomerToAccount(newCustomer);
    closeAddCustomerSearch();
  }

  function changePlayerHandleCustomerSearch(query: string) {
    setChangeCustomerState(prevState => ({ ...prevState, customerQuery: query }));
  }

  function handleChangePlayerSelection(id: number, customer: ICustomer) {
    setChangeCustomerState(prevState => ({ ...prevState, selectedCustomer: customer }));
  }

  function openNewCustomerSheet() {
    setChangeCustomerState(prevState => ({
      ...prevState,
      showNewCustomer: true,
    }));
  }

  const hasCustomer = !!account?.customer?.id;
  const hasPaymentMethods = (paymentMethods?.length ?? 0) > 0;
  const accountCustomers = account?.customers ?? [];
  const canAddCustomer = accountCustomers.length < MAX_ACCOUNT_CUSTOMERS;
  const showCustomerLimitAlert = !canAddCustomer && !customerLimitAlertDismissed;
  const filteredCustomerSearchResults = (changeCustomerState.customerSearchResult ?? []).filter(customer => {
    if (!customer?.id) {
      return false;
    }
    if (changeCustomerState.mode === "replace" && customer.id === changeCustomerState.replaceCustomerId) {
      return true;
    }
    return !accountCustomers.some(existing => existing.id === customer.id);
  });

  return (
    <>
      <Page
        title={t("Edit Account")}
        splitLayout
        breadcrumbs={[
          { label: t("Settings"), url: "/admin/settings" },
          { label: t("Accounting"), url: "/admin/settings/accounts" },
          { label: t("Accounts"), url: backToAccountsUrl },
          { label: t("Account overview"), url: backToOverviewUrl },
          { label: t("Edit Account"), url: editAccountUrl },
        ]}
        notificationBarProps={{
          isVisible: unsavedChangesExist(),
          onAction: saveAccount,
          onCancel: cancelUnsavedChanges,
        }}
      >
        {account && accountingReferenceTypes ? (
          <>
            <Page.Section twoThirds>
              <Card>
                <Card.Section>
                  <Form>
                    <FormLayout>
                      <FormLayout.Group>
                        <Input value={account.title} label={t("Title")} id="title" onChange={handleChange} />
                        <Input
                          label={t("Account Number")}
                          id="account_number"
                          value={account.account_number}
                          onChange={handleChange}
                        />
                      </FormLayout.Group>
                      <FormLayout.Group>
                        <Input
                          value={account.limit}
                          label={t("Limit")}
                          id={!account.limit_is_valid ? "inputError" : ""}
                          onChange={e => handleNumberChange(e, "limit", "limit_is_valid")}
                        />
                      </FormLayout.Group>
                      <FormLayout.Group>
                        <Select
                          label={t("Accounting Reference")}
                          defaultValue={Number(account.accounting_reference_id)}
                          onChange={handleDropDownChange}
                        >
                          {accountingReferenceTypes?.map(
                            (type: { id: number; title: string; reference_number?: string | null }, index: number) => {
                              const label = formatAccountingReferenceLabel(type.title ?? "", type.reference_number);
                              return (
                                <Option key={index} value={type.id} name={label}>
                                  <span>{label}</span>
                                </Option>
                              );
                            },
                          )}
                        </Select>
                      </FormLayout.Group>
                      <FormLayout.Group>
                        <BatchOptionGroup
                          aria-label={t("Account options")}
                          options={[
                            ...(showMultiCourseOption
                              ? [
                                  {
                                    key: "global",
                                    title: t("Multi-course account"),
                                    description: t("This account can be used across all courses."),
                                    selected: account.global,
                                    onSelect: () => setAccount(prev => ({ ...prev, global: !prev.global })),
                                  },
                                ]
                              : []),
                            {
                              key: "sendStatement",
                              title: t("Send monthly statements"),
                              description: t(
                                "On the 5th of every month, the customer will receive the previous month's statement via email.",
                              ),
                              selected: account.sendStatement,
                              onSelect: () =>
                                setAccount(prev => ({ ...prev, sendStatement: !prev.sendStatement })),
                            },
                            {
                              key: "auto_pay",
                              title: t("Auto pay balance"),
                              description: t(
                                "Automatically charge the account's default payment method for the outstanding balance.",
                              ),
                              selected: account.auto_pay,
                              onSelect: () => setAccount(prev => ({ ...prev, auto_pay: !prev.auto_pay })),
                              content: (
                                <Input
                                  label={t("Auto Pay Date")}
                                  type="number"
                                  id="auto_pay_anchor_date"
                                  value={
                                    account.auto_pay_anchor_date < minimumDayInput
                                      ? ""
                                      : account.auto_pay_anchor_date > maximumDayInput
                                      ? maximumDayInput
                                      : account.auto_pay_anchor_date
                                  }
                                  onChange={e =>
                                    setAccount(prev => ({
                                      ...prev,
                                      auto_pay_anchor_date: Number(e.target?.value),
                                    }))
                                  }
                                  prefix={t("Day of Month")}
                                  error={account.auto_pay_anchor_date === 0}
                                />
                              ),
                            },
                          ]}
                        />
                      </FormLayout.Group>

                      {hasCustomer ? (
                        <FormLayout.Group>
                          {paymentMethodsLoading ? (
                            <p className="table-cell-support-text">{t("Loading payment methods…")}</p>
                          ) : hasPaymentMethods ? (
                            <Select
                              key={`pm-${account.customer.id}-${account.default_payment_method_id ?? "none"}`}
                              label={t("Default Payment Method")}
                              defaultValue={account.default_payment_method_id ?? -1}
                              onChange={(value: number) =>
                                setAccount(prev => ({
                                  ...prev,
                                  default_payment_method_id: value === -1 ? null : value,
                                }))
                              }
                              helpText={t(
                                "Used for auto pay and batch account charges when charging a saved card.",
                              )}
                            >
                              <Option value={-1} name={t("None")}>
                                {t("None")}
                              </Option>
                              {paymentMethods.map(method => (
                                <Option key={method.id} value={method.id} name={paymentMethodLabel(method)}>
                                  {PaymentMethodOption(method)}
                                </Option>
                              ))}
                            </Select>
                          ) : (
                            <Callout
                              type="error"
                              title={t("No Saved Payment Methods")}
                              content={t(
                                "This customer does not have any payment methods saved. Add a card on the customer profile to set a default for this account.",
                              )}
                            />
                          )}
                        </FormLayout.Group>
                      ) : (
                        <FormLayout.Group>
                          <Callout
                            type="info"
                            title={t("Customer Required")}
                            content={t(
                              "Assign a customer to this account to choose a default payment method.",
                            )}
                          />
                        </FormLayout.Group>
                      )}
                    </FormLayout>
                  </Form>
                </Card.Section>
              </Card>

            </Page.Section>
            <Page.Section oneThird>
              <Card className="account-edit-customers-card">
                <Card.Section>
                  <div className="account-edit-customers">
                    <div className="account-edit-customers__header">
                      <div className="account-edit-customers__header-copy">
                        <h3 className="account-edit-customers__title">{t("Customer(s)")}</h3>
                        <p className="account-edit-customers__description">
                          {t(
                            "Add, change or remove customers to share this account’s balance, credit limit and transactions.",
                          )}
                        </p>
                      </div>
                      <span className="account-edit-customers__count">
                        {accountCustomers.length} / {MAX_ACCOUNT_CUSTOMERS}
                      </span>
                    </div>

                    {showCustomerLimitAlert ? (
                      <div className="account-edit-customers__limit-alert">
                        <Callout
                          type="alerts"
                          iconAlignTop
                          title={t("Customer limit reached")}
                          content={t(
                            "This house account has reached the maximum of {{max}} customers. Remove one to assign another.",
                            { max: MAX_ACCOUNT_CUSTOMERS },
                          )}
                          onClose={() => setCustomerLimitAlertDismissed(true)}
                        />
                      </div>
                    ) : null}

                    {accountCustomers.length > 0 ? (
                      <ul className="account-edit-customers__list">
                        {accountCustomers.map(customer => (
                          <li key={customer.id} className="account-edit-customers__row">
                            <div className="account-edit-customers__identity">
                              <div className="account-edit-customers__avatar" aria-hidden>
                                {getCustomerInitials(customer)}
                              </div>
                              <div className="account-edit-customers__details">
                                <span className="account-edit-customers__name">
                                  {customer.full_name || "—"}
                                </span>
                                <span className="account-edit-customers__email">
                                  {customer.email || "—"}
                                </span>
                              </div>
                            </div>
                            <div className="account-edit-customers__actions">
                              <button
                                type="button"
                                className="account-edit-customers__icon-button account-edit-customers__icon-button--danger"
                                aria-label={t("Remove customer")}
                                onClick={() => removeAccountCustomer(customer.id)}
                              >
                                <FontAwesomeIcon icon={faTrashCan} />
                              </button>
                              <button
                                type="button"
                                className="account-edit-customers__icon-button"
                                aria-label={t("Change customer")}
                                onClick={() => handleShowCustomerChange(customer)}
                              >
                                <FontAwesomeIcon icon={faPen} />
                              </button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="account-edit-customers__empty">{t("No customer attached")}</p>
                    )}

                    <div className="account-edit-customers__footer">
                      {showAddCustomerSearch && canAddCustomer ? (
                        <div className="account-edit-customers__add-search">
                          <Select
                            showSearch
                            label={t("Customer")}
                            className="flex justify-center align-center w-full h-10 position-relative z-20 text-black text-medium appearance-none border-none focus:outline-none placeholder-gray-200"
                            onSearch={(query: string) => changePlayerHandleCustomerSearch(query)}
                            onChange={(id: number, customer: ICustomer) => handleAddCustomerSelection(id, customer)}
                            placeholder={t("Search by name or email address...")}
                            allowClear
                            searchValue={changeCustomerState.customerQuery}
                            showDropDownOnFocus={true}
                            searching={changeCustomerState.customerSearching}
                            autoFocus
                          >
                            <div
                              className="ui-select-dropdown-list-item"
                              onClick={openNewCustomerSheet}
                            >
                              <p>{t("New customer")}</p>
                            </div>
                            {filteredCustomerSearchResults.map((customer, index) => (
                              <Option
                                key={index}
                                value={customer.id}
                                name={customer.full_name}
                                extraValues={customer}
                              >
                                <div className="flex justify-between">
                                  <div>
                                    <div className="text-semibold text-lg">{customer?.full_name}</div>
                                    <div className="text-sm text-gray-500">{customer.email}</div>
                                    <div className="text-sm text-gray-500">
                                      {customer.phone ? customer.phone : null}
                                    </div>
                                  </div>
                                </div>
                              </Option>
                            ))}
                          </Select>
                        </div>
                      ) : canAddCustomer ? (
                        <button
                          type="button"
                          className="account-edit-customers__add-button"
                          onClick={openAddCustomerSearch}
                        >
                          <FontAwesomeIcon icon={faPlus} className="account-edit-customers__add-button-icon mr-2" />
                          <span>{t("Add customer")}</span>
                        </button>
                      ) : (
                        <OverlayTrigger
                          placement="right"
                          overlay={
                            <Tooltip
                              id="account-edit-add-customer-disabled"
                              className="account-edit-customers__limit-tooltip"
                            >
                              {t("Customer limit reached to")}
                              <br />
                              {t("maximum of {{max}}.", { max: MAX_ACCOUNT_CUSTOMERS })}
                              <br />
                              {t("Remove one to add another.")}
                            </Tooltip>
                          }
                        >
                          <span className="account-edit-customers__add-button-wrap">
                            <button
                              type="button"
                              className="account-edit-customers__add-button"
                              disabled
                            >
                              <FontAwesomeIcon
                                icon={faPlus}
                                className="account-edit-customers__add-button-icon mr-2"
                              />
                              <span>{t("Add customer")}</span>
                            </button>
                          </span>
                        </OverlayTrigger>
                      )}
                    </div>
                  </div>
                </Card.Section>
              </Card>
            </Page.Section>
          </>
        ) : null}
      </Page>

      <ChangeCustomer
        title={t("Change customer")}
        currentCustomerTitle={t("Current customer:")}
        newCustomerTitle={t("Replace with new customer:")}
        okText={t("Change customer")}
        open={changeCustomerState.showChangeCustomer}
        changeCustomerState={changeCustomerState}
        searching={changeCustomerState.customerSearching}
        searchResults={filteredCustomerSearchResults}
        newCustomerActive={changeCustomerState.showNewCustomer && changeCustomerState.showChangeCustomer}
        contextLabel={account?.title}
        onCancel={closeChangePlayer}
        onOk={applyCustomerSelection}
        handleSearchQueryChange={changePlayerHandleCustomerSearch}
        handleCustomerSelection={handleChangePlayerSelection}
        handleRemoveSelectedCustomer={() =>
          setChangeCustomerState(prevState => ({
            ...prevState,
            selectedCustomer: null,
            customerSearchResult: [],
            customerQuery: "",
          }))
        }
        handleCreateNewCustomer={createNewCustomer}
        handleCloseCreateNewCustomer={() =>
          setChangeCustomerState(prevState => ({
            ...prevState,
            showNewCustomer: false,
          }))
        }
        handleNewCustomerActive={openNewCustomerSheet}
      />

      <NewCustomer
        newCustomerSheetActive={changeCustomerState.showNewCustomer && !changeCustomerState.showChangeCustomer}
        onCancel={() =>
          setChangeCustomerState(prevState => ({
            ...prevState,
            showNewCustomer: false,
          }))
        }
        onOk={createNewCustomer}
        searchValue={changeCustomerState.customerQuery}
      />
    </>
  );
}
